import React, {useMemo, useState} from 'react';
import {Modal, Form, Input, DatePicker, Select, message} from 'antd';
import type {Dayjs} from 'dayjs';
import dayjs from 'dayjs';

export type TaskPriority = 'LOW' | 'MEDIUM' | 'HIGH';

export interface TaskFormValues {
    title: string;
    description?: string;
    dueDate?: Dayjs;
    priority: TaskPriority;
}

export interface NewTaskPayload {
    title: string;
    description?: string;
    /** ISO string in UTC (e.g., 2025-08-21T12:00:00.000Z) */
    dueDate?: string;
    priority: TaskPriority;
}

interface TaskModalProps {
    open: boolean;
    onCancel: () => void;
    /**
     * Called after validation. If it resolves, the modal will close and the form resets.
     * Throw or reject to keep the modal open and show an error.
     */
    onCreated?: (payload: NewTaskPayload) => Promise<unknown> | unknown;
    /** Optional initial values (useful for duplicates/templates) */
    initialValues?: Partial<TaskFormValues>;
}

const priorities: { label: string; value: TaskPriority }[] = [
    {label: 'Basse', value: 'LOW'},
    {label: 'Moyenne', value: 'MEDIUM'},
    {label: 'Haute', value: 'HIGH'},
];

const TaskModal: React.FC<TaskModalProps> = ({open, onCancel, onCreated, initialValues}) => {
    const [form] = Form.useForm<TaskFormValues>();
    const [submitting, setSubmitting] = useState(false);

    const mergedInitials = useMemo<TaskFormValues>(
        () => ({
            title: '',
            description: '',
            priority: 'MEDIUM',
            ...initialValues,
        }),
        [initialValues]
    );

    const handleFinish = async (values: TaskFormValues) => {
        const payload: NewTaskPayload = {
            title: values.title.trim(),
            description: values.description?.trim() || undefined,
            dueDate: values.dueDate ? values.dueDate.toDate().toISOString() : undefined,
            priority: values.priority,
        };

        try {
            setSubmitting(true);
            if (onCreated) {
                console.log('Creating task with payload:', payload);
                await onCreated(payload);
            }
            message.success('Tâche créée');
            form.resetFields();
            onCancel();
        } catch (e: any) {
            const msg = e?.message || "Échec de la création de la tâche";
            message.error(msg);
        } finally {
            setSubmitting(false);
        }
    };

    const disabledDate = (current: Dayjs) => current && current < dayjs().startOf('day');

    return (
        <Modal
            open={open}
            title="Nouvelle tâche"
            onCancel={() => {
                if (!submitting) onCancel();
            }}
            onOk={() => form.submit()}
            okText="Créer"
            destroyOnClose
            maskClosable={false}
            confirmLoading={submitting}
        >
            <Form
                form={form}
                layout="vertical"
                initialValues={mergedInitials}
                onFinish={handleFinish}
            >
                <Form.Item
                    label="Titre"
                    name="title"
                    rules={[
                        {required: true, message: 'Le titre est requis'},
                        {max: 120, message: '120 caractères maximum'},
                    ]}
                >
                    <Input autoFocus allowClear placeholder="Ex. Préparer le rapport mensuel"/>
                </Form.Item>

                <Form.Item label="Description" name="description"
                           rules={[{max: 2000, message: '2000 caractères maximum'}]}>
                    <Input.TextArea allowClear placeholder="Détails, sous-tâches, liens…"
                                    autoSize={{minRows: 3, maxRows: 8}}/>
                </Form.Item>

                <Form.Item label="Échéance" name="dueDate">
                    <DatePicker
                        style={{width: '100%'}}
                        showTime
                        format="YYYY-MM-DD HH:mm"
                        disabledDate={disabledDate}
                        placeholder="Choisir une date et une heure"
                    />
                </Form.Item>

                <Form.Item label="Priorité" name="priority">
                    <Select options={priorities}/>
                </Form.Item>
            </Form>
        </Modal>
    );
};

export default TaskModal;
